import styled, { createGlobalStyle } from 'styled-components';
import { TextButton } from 'ui/button';
import { StickyPageHeader } from 'ui/pageHeader';
import { PAGE_HEADER_VARIANTS } from 'ui/pageHeader/constants';
import {
  Caption,
  baseColors,
  body1,
  body2,
  borderColors,
  caption,
  colors,
  heading5,
  smallShadow,
  spacing,
  subtitle1,
} from 'ui/theme';
import { Tooltip } from 'ui/tooltip';
import { pxToRem } from 'utils/convert';

export const GlobalBodyStyles = createGlobalStyle`
  body {
    overflow: hidden !important;
  }
`;

export const DividedList = styled.div`
  ${caption};
  align-items: center;
  color: ${colors.textSecondary};
  display: flex;
  gap: ${spacing.half};
  flex-wrap: wrap;
`;

export const DividedListElement = styled.div`
  display: flex;
  min-width: 0;

  white-space: nowrap;
  &:not(:last-child):after {
    padding-left: ${spacing.half};
    content: '•';
    color: ${colors.grey35};
  }

  &:last-child {
    padding-right: 0;
  }
`;

export const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  position: relative;
  overflow: hidden;
`;

export const BodyHeaderContainer = styled(StickyPageHeader)`
  &[data-variant=${PAGE_HEADER_VARIANTS.STICKY}] {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-content: space-between;
    gap: ${spacing.threeQuarter};
    padding: ${spacing.plusHalf};
    margin: -1.4285714285714286rem -2.2857142857142856rem 0 0;
    &:not([data-scrolly='0']) {
      ${smallShadow}
    }
  }
  [data-test='page-header-content'] {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  & > div {
    width: 100%;
  }
`;

export const BodyHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${spacing.half};
`;

export const Title = styled.div`
  ${heading5}
  flex: 1 0 0;
  height: ${pxToRem(26)};
`;

export const PlacementTitle = styled.div`
  ${body1}
  flex: 1 0 0;
`;

export const DescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

export const Description = styled.div`
  ${body2};
  color: ${colors.textPrimary};
  word-break: break-word;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const DisplayPriorityEditContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: ${pxToRem(760)};
  height: fit-content;
  gap: ${spacing.threeQuarter};
  padding: ${spacing.plusHalf};
  margin-bottom: ${spacing.x8};
  border-radius: ${spacing.half};
`;

export const DisplayPriorityOrderFrame = styled.div`
  position: relative;
  display: grid;
  border-left: solid 1px ${borderColors.neutral300};
  padding: ${spacing.base} 0 ${spacing.base} ${spacing.plusHalf};
  gap: ${spacing.half};
  margin: -${spacing.base} 0;
  min-width: ${pxToRem(1000)};
`;

export const StyledCaption = styled(Caption)`
  position: relative;
  margin-left: ${spacing.plusHalf};
  color: ${colors.textSecondary};

  &:before {
    content: url('data:image/svg+xml;utf8,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%227%22%20height%3D%226%22%20viewBox%3D%220%200%207%206%22%20fill%3D%22none%22%3E%0A%20%20%20%20%3Ccircle%20cx%3D%223.5%22%20cy%3D%223%22%20r%3D%222.5%22%20fill%3D%22white%22%20stroke%3D%22%23787174%22/%3E%0A%3C/svg%3E');
    display: inline-block;
    width: ${spacing.threeQuarter};
    height: ${spacing.threeQuarter};
    position: absolute;
    top: -${spacing.single};
    left: -${pxToRem(27)}; /** 6px circle, so needs 3px extra to be centered on the border-left */
  }
`;

export const StyledHeader = styled.div`
  ${subtitle1}
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${spacing.quarter};
`;

export const TakeoverGrid = styled.div<{ navBarHeight: number }>`
  display: grid;
  grid-template-columns: 1fr ${pxToRem(316)};
  grid-template-rows: ${spacing.x4} 100%;
  height: ${pxToRem(760)};
  margin-top: ${pxToRem(80)};

  /* to offset the sticky header */
  padding-top: ${({ navBarHeight }) => pxToRem(navBarHeight)};

  &:not([data-scrolly='0']) {
    padding-top: ${({ navBarHeight }) => pxToRem(navBarHeight / 2)};
  }
`;

export const PlacementPreview = styled.div`
  grid-column-start: 2;
  grid-column-end: span 2;
  grid-row-start: 2;
  grid-row-end: span 2;
  padding-top: ${spacing.double};
  padding-bottom: ${spacing.doubleHalf};
  overflow-y: scroll;
`;

export const PlacementPreviewTitleCell = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0px ${spacing.half};
  grid-column-start: 2;
  grid-column-end: span 2;
  grid-row-start: 1;
  grid-row-end: span 1;
  border-bottom: 1px solid ${baseColors.grey15};
`;

export const CampaignColumnsGrid = styled.div`
  min-width: ${pxToRem(1200)};
  border: 1px 0px 1px 0px;
  height: ${spacing.x4};
  border-bottom: 1px solid ${baseColors.grey15};
  background: white;
  display: grid;
  grid-template-columns: 474px 250px 130px 1fr;
  overflow-x: hidden;
`;

export const ScrollableContainer = styled.div`
  height: 100%;
  overflow: scroll;
  grid-column-start: 1;
  grid-column-end: 1;
  grid-row-start: 1;
  grid-row-end: span 2;
  -ms-overflow-style: none;
  scrollbar-width: none;
  background: ${colors.backgroundSecondary};
  border-right: 1px solid ${baseColors.grey15};
  ::-webkit-scrollbar {
    display: none;
  }
`;

export const CampaignTitle = styled.span`
  ${body1}
  grid-column-start: 1;
  grid-column-end: span 1;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin-left: ${spacing.x5};
`;

export const StartDate = styled.span`
  ${body1}
  grid-column-start: 3;
  grid-column-end: span 1;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`;

export const Eligible = styled.span`
  ${body1}
  grid-column-start: 2;
  grid-column-end: span 1;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  padding-left: ${spacing.half};
`;

export const DisplayUntil = styled.span`
  ${body1}
  grid-column-start: 4;
  grid-column-end: span 1;

  display: flex;
  min-width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  padding-right: ${spacing.x3};
  overflow: hidden;
  white-space: nowrap;
`;

export const GreyCaption = styled(Caption)`
  color: ${colors.textSecondary};
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const StyledCampaignLimitInfo = styled.div`
  display: flex;
  gap: ${spacing.half};
  justify-content: center;
  align-items: center;
`;

export const CampaignLimit = styled(GreyCaption)`
  ${body1}
  color: ${colors.textSecondary};
`;

export const StyledTooltip = styled(Tooltip)`
  display: flex;
`;

export const StyledTextButton = styled(TextButton)`
  align-self: center;
  width: ${spacing.x8};

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
